import { Modal, ModalBody, ModalHeader, Button } from 'reactstrap';
import { Link } from 'react-router-dom-latest';
import { PROFILE_SETTINGS_MENU, THIRD_PARTY_APPS } from 'constants/constants';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import BoxExportPicker from 'common/Box/ExportPicker';
import SmartVaultWidget from 'common/SmartVault/widget';
import CustomImage from 'common/CustomImage';
import styled from '@emotion/styled';
import SharePointPicker from 'common/SharePoint/Picker';
import GooglePicker from 'common/GooglePicker';
import GruntWorxExportPicker from 'common/GruntWorx/ExportPicker';
import { useGruntWorxExport } from 'common/GruntWorx/useGruntWorxExport';
import { ZapierExportPicker, useZapierExport } from 'common/Zapier';
import { AlertMessage } from 'utilities/utils';

type ExportModalProps = {
  selectedRecords: { uuid: string; id: string }[];
  closeModal: () => void;
  initialModal?: string; // Optional: pre-select an export option (e.g., 'zapier', 'sharepoint')
};

type ThirdPartyApps = {
  id: string;
  name: string;
  access_token?: string; // Optional: only needed for OAuth-based integrations (Box, Google Drive, etc.)
}[];

export default function ExportModal({
  selectedRecords,
  closeModal,
  initialModal,
}: ExportModalProps) {
  const supportedApps = useMemo(
    () => [
      'smartvault',
      'googledrive',
      'sharepoint',
      'onedrive',
      'box',
      'gruntworx',
      'zapier',
    ],
    [],
  );

  const defaultMessage = (
    <>
      No destinations configured! Please see our
      <Link to={`${PROFILE_SETTINGS_MENU}?tab=third_party_apps`}>
        Third Party Integrations
      </Link>
      page to browse all available connections.
    </>
  );

  const [thirdPartyApps, setThirdPartyApps] = useState<ThirdPartyApps>([]);
  const [selectedModal, setSelectedModal] = useState(initialModal || '');
  const [showDefaultMessage, setShowDefaultMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [regularApps, setRegularApps] = useState<ThirdPartyApps>([]);
  const [regularAppsLoaded, setRegularAppsLoaded] = useState(false);
  const handleModalClose = () => closeModal();

  // Use the GruntWorx hook to get connection status
  const { connectionStatus } = useGruntWorxExport();

  // Use the Zapier hook to check if export is configured
  const { isConfigured: isZapierConfigured, isLoading: isZapierLoading } =
    useZapierExport();

  const fetchRegularThirdPartyApps = useCallback(async () => {
    try {
      // Fetch regular third-party apps (excluding GruntWorx)
      const thirdPartyResponse = await axios.get(THIRD_PARTY_APPS);
      const filteredThirdPartyApps = thirdPartyResponse.data.data.filter(
        (app: any) =>
          supportedApps.includes(app.id) &&
          app.token_exits &&
          app.id !== 'gruntworx',
      );
      setRegularApps(filteredThirdPartyApps);
      setRegularAppsLoaded(true);
    } catch (error: any) {
      AlertMessage('error', error.message, 3000);
      setRegularAppsLoaded(true); // Set loaded even on error to prevent hanging
    }
  }, [supportedApps]);

  const updateThirdPartyApps = useCallback(() => {
    // Add GruntWorx if it's active
    let gruntworxApp = null;
    if (connectionStatus === 'Active') {
      gruntworxApp = {
        id: 'gruntworx',
        name: 'GruntWorx',
        // No access_token needed - GruntWorx uses connection status, not OAuth
      };
    }

    // Add Zapier if it's configured
    let zapierApp = null;
    if (isZapierConfigured) {
      zapierApp = {
        id: 'zapier',
        name: 'Zapier',
        // No access_token needed - Zapier uses webhook URL configuration, not OAuth
      };
    }

    // Combine all apps
    const allApps = [
      ...regularApps,
      ...(gruntworxApp ? [gruntworxApp] : []),
      ...(zapierApp ? [zapierApp] : []),
    ];
    setThirdPartyApps(allApps);

    if (allApps.length === 0) {
      setShowDefaultMessage(true);
    }

    setIsLoading(false);
  }, [connectionStatus, regularApps, isZapierConfigured]);

  useEffect(() => {
    fetchRegularThirdPartyApps();
  }, [fetchRegularThirdPartyApps]);

  // Update apps list when GruntWorx, Zapier, and regular apps are all loaded
  useEffect(() => {
    if (connectionStatus !== null && regularAppsLoaded && !isZapierLoading) {
      updateThirdPartyApps();
    }
  }, [connectionStatus, regularApps, regularAppsLoaded, isZapierLoading, updateThirdPartyApps]);

  useEffect(() => {
    // If initialModal is provided, set it immediately
    if (initialModal && selectedModal !== initialModal) {
      setSelectedModal(initialModal);
    }
    // Auto-select GruntWorx if it's the only option - but only after everything is loaded
    else if (
      !isLoading &&
      !initialModal &&
      selectedModal === '' &&
      thirdPartyApps.length === 1 &&
      thirdPartyApps[0].id === 'gruntworx'
    ) {
      setSelectedModal('gruntworx');
    }
  }, [isLoading, thirdPartyApps, selectedModal, initialModal]);

  const loading = (
    <div style={{ height: '200px' }}>
      <div
        id="loading"
        data-testid="loading"
        className="d-block"
        role="status"
        aria-label="Loading document"
      />
    </div>
  );

  const IntegrationOption = styled.div`
    border-radius: 0.25em;
    &:hover {
      cursor: pointer;
      background-color: #f2f2f2 !important;
    }
  `;

  const modalBody = (
    <>
      <div className="modalContent--inner pb-0">
        {showDefaultMessage && defaultMessage}
        {thirdPartyApps.map((app) => (
          <IntegrationOption
            className="p-2"
            key={app.id}
            onClick={() => setSelectedModal(app.id)}
          >
            <CustomImage
              alt="logo"
              className="mr-2 mb-1"
              src={`${app.id}.png`}
              style={{ width: '24px', height: '24px' }}
            />
            <span className="pointer text-small">{app.name}</span>
          </IntegrationOption>
        ))}
      </div>
      <div className="row">
        <div className="col-12 justify-content-end formbtn mb-4">
          <Button
            className="btn btn-outline-light mr-4"
            onClick={handleModalClose}
          >
            Cancel
          </Button>
        </div>
      </div>
    </>
  );

  const pickerModal = (
    <Modal
      isOpen
      className="customModal customModal--center customModal--alert customModal--w600 customModal--nopadding"
    >
      <ModalHeader className="ModalHeader">
        <button
          type="button"
          onClick={handleModalClose}
          className="close"
          aria-label="Close"
        >
          <i className="icon-close2" />
        </button>
      </ModalHeader>
      <ModalHeader className="modalHeader">
        <div className="text-medium pl-2">
          <div>Select Destination</div>
        </div>
      </ModalHeader>
      <ModalBody className="modalContent">
        {isLoading ? loading : modalBody}
      </ModalBody>
    </Modal>
  );

  return (
    <>
      {selectedModal === '' && pickerModal}
      {selectedModal === 'smartvault' && (
        <SmartVaultWidget
          mode="export"
          selectedRecords={selectedRecords}
          cancelWidget={closeModal}
        />
      )}
      {selectedModal === 'googledrive' && (
        <GooglePicker
          mode="export"
          selectedRecords={selectedRecords}
          cancelWidget={closeModal}
        />
      )}
      {(selectedModal === 'sharepoint' || selectedModal === 'onedrive') && (
        <SharePointPicker
          mode="export"
          appKey={selectedModal}
          selectedRecords={selectedRecords}
          closeModal={closeModal}
          cancelWidget={setSelectedModal}
        />
      )}
      {selectedModal === 'box' && (
        <BoxExportPicker
          selectedRecords={selectedRecords}
          cancelWidget={closeModal}
        />
      )}
      {selectedModal === 'gruntworx' && (
        <GruntWorxExportPicker
          selectedRecords={selectedRecords.map((record) => ({
            ...record,
            uuid: record.uuid,
          }))}
          cancelWidget={closeModal}
        />
      )}
      {selectedModal === 'zapier' && (
        <ZapierExportPicker
          selectedRecords={selectedRecords}
          cancelWidget={closeModal}
        />
      )}
    </>
  );
}
